/**
 * Turntable — the 360° studio-render viewer, its still, and the card thumbnail.
 *
 * Port of the demo's `Turntable` class, `turnStill()` and `turnIcon()`
 * (`OEM+_Platform_Demo.html` lines 1753–1831). The frames are the demo's own
 * renders, extracted to `public/turntable/<MODEL>/` by `npm run oem:extract`
 * and fetched per model when a viewer mounts — none of the 8.4 MB is in the
 * bundle.
 *
 * | Demo | Here |
 * |---|---|
 * | `build()` — stage, controls, snapshot strip | {@link Turntable} |
 * | drag to rotate, 14 px a frame (60 exploded) | same; plus ◀ ▶, arrow keys and horizontal scroll |
 * | `toggleAuto()` — 110 ms a frame, 600 ms exploded | same; off under `prefers-reduced-motion` |
 * | `toggleExplode()` — keep the angle, swap to 6 frames | same |
 * | `glow()` — mask pixels of the selected component, blurred band colour | same, on a canvas |
 * | `overlay()` — callouts for flagged components, draggable labels | same; offsets kept for the session |
 * | `turnStill()` | {@link TurntableStill} |
 * | `turnIcon()` | {@link MachineThumb} |
 *
 * ## Deviations from the demo
 *
 * 1. **Poster first.** The demo inlines every frame, so its first paint is
 *    frame 18. Here the model's side JPG (`public/machines/side/`) shows until
 *    the first frame has loaded. A server or snapshot render never loads
 *    frames, so its markup is the poster and the controls — deterministic.
 * 2. **Livery.** Under the Komatsu brand the pink Meridian paint is recoloured
 *    to Komatsu yellow in a canvas before display; see `turntable-assets.ts`.
 * 3. **Keyboard.** ◀ ▶ step, the stage takes ←/→ and Home (back to the side
 *    elevation), and every callout is a focusable button. The demo is
 *    pointer-only.
 * 4. **Scroll rotates only sideways.** A horizontal trackpad swipe or
 *    shift + wheel turns the machine; a plain vertical wheel still scrolls the
 *    page.
 *
 * UI layer. Frame/angle arithmetic and callout placement live in
 * `Turntable.transform.ts`.
 *
 * @see src/versions/v1/shared/viz/Turntable.transform.ts
 * @see src/versions/v1/shared/viz/turntable-assets.ts
 * @see docs/logic/fleet/machine-page.md
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { cn } from '@/shared/cn';
import type { ComponentHealth } from '../../data/core';
import { sideViewModel, type Machine } from '../../schema/machine';
import { useBrand } from '../BrandContext';
import { healthBand } from '../oem-engine';
import { hasIllustration, MachineIllustration } from './MachineIllustration';
import {
  CALLOUT_H,
  flaggedComponents,
  frameAngle,
  frameIndex,
  layoutCallouts,
  maskTarget,
  nearestSnap,
  stepFrame,
  stillCallouts,
  TURN_HOME_DEG,
  TURN_SNAPS,
  type CalloutOffset,
  type FrameSet,
  type TurnCallout,
} from './Turntable.transform';
import {
  driveKind,
  LIVERY_CSS_FALLBACK,
  liveryPlan,
  turntableFrameUrl,
  turntableIconUrl,
  turntableModel,
  useLiveryUrls,
  useTurntableManifest,
} from './turntable-assets';
import { useReducedMotion } from './useReducedMotion';

export { hasTurntable } from './turntable-assets';

/** The machine fields a viewer reads. */
type TurnMachine = Pick<Machine, 'machine_id' | 'model' | 'cls' | 'battery' | 'autonomous'>;

/** Demo line 1761. `BANDHEX` — the glow colours when a band token cannot be read. */
const BANDHEX: Readonly<Record<string, string>> = {
  major: '#2B1B2E',
  vpoor: '#D32F2F',
  poor: '#E67E22',
  fair: '#B58900',
  good: '#1E8449',
};

/** Demo line 1782. Pointer travel per frame, px. */
const DRAG_STEP = 14;
const DRAG_STEP_EXPLODED = 60;
/** Demo line 1792. Auto-rotate period, ms. */
const SPIN_MS = 110;
const SPIN_MS_EXPLODED = 600;
/** Horizontal wheel travel per frame, px. */
const WHEEL_STEP = 40;

/** Demo `TURNPOS` (line 1759) — dragged label offsets, per machine, for the session. */
const TURNPOS = new Map<string, Record<string, CalloutOffset>>();

const posterUrl = (f: TurnMachine) => `/machines/side/${sideViewModel(f.model)}.jpg`;

function hexToRgb(hex: string): [number, number, number] | null {
  const m = /^#([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** The glow colour for a component: its band token, read from the page, else `BANDHEX`. */
function glowRgb(
  el: Element,
  health: readonly ComponentHealth[],
  component: string,
): [number, number, number] {
  const row = health.find((h) => h.component === component);
  const band = row ? healthBand(row) : null;
  const css = getComputedStyle(el);
  const token = band ? css.getPropertyValue(`--band-${band}`) : css.getPropertyValue('--accent');
  return hexToRgb(token) ?? hexToRgb(band ? BANDHEX[band] : '#A3123F') ?? [163, 18, 63];
}

/**
 * Demo lines 1797–1807. `glow()` — keep the mask pixels whose red channel is
 * the component's key, fill them with its colour, and draw them twice blurred
 * for the halo and once sharp. Returns the pixel count (0 → hide the canvas).
 */
function drawGlow(
  cv: HTMLCanvasElement,
  mask: HTMLImageElement,
  target: number,
  rgb: readonly number[],
): number {
  const w = mask.naturalWidth;
  const h = mask.naturalHeight;
  if (!w || !h) return 0;
  if (cv.width !== w || cv.height !== h) {
    cv.width = w;
    cv.height = h;
  }
  const off = document.createElement('canvas');
  off.width = w;
  off.height = h;
  const oc = off.getContext('2d', { willReadFrequently: true });
  const ctx = cv.getContext('2d');
  if (!oc || !ctx) return 0;
  oc.drawImage(mask, 0, 0);
  const id = oc.getImageData(0, 0, w, h);
  const px = id.data;
  let n = 0;
  for (let p = 0; p < px.length; p += 4) {
    if (px[p + 3] > 40 && Math.abs(px[p] - target) <= 5) {
      px[p] = rgb[0];
      px[p + 1] = rgb[1];
      px[p + 2] = rgb[2];
      px[p + 3] = 255;
      n++;
    } else px[p + 3] = 0;
  }
  oc.putImageData(id, 0, 0);
  ctx.clearRect(0, 0, w, h);
  ctx.save();
  ctx.filter = 'blur(9px)';
  ctx.globalAlpha = 0.9;
  ctx.drawImage(off, 0, 0);
  ctx.drawImage(off, 0, 0);
  ctx.restore();
  ctx.save();
  ctx.filter = 'blur(1px)';
  ctx.globalAlpha = 0.82;
  ctx.drawImage(off, 0, 0);
  ctx.restore();
  return n;
}

/** Load (and keep) one image per URL, for the glow's masks. */
function useImageCache() {
  const cache = useRef(new Map<string, HTMLImageElement>());
  return useCallback((src: string) => {
    let im = cache.current.get(src);
    if (!im) {
      im = new Image();
      im.src = src;
      cache.current.set(src, im);
    }
    return im;
  }, []);
}

/** Warm the browser cache for a frame set so rotation never waits on the network. */
function usePreload(urls: readonly string[] | null) {
  useEffect(() => {
    if (!urls) return;
    const held = urls.map((u) => {
      const im = new Image();
      im.decoding = 'async';
      im.src = u;
      return im;
    });
    return () => {
      held.length = 0;
    };
  }, [urls]);
}

// ---------------------------------------------------------------------------
// Callout overlay (shared by the viewer and the still)
// ---------------------------------------------------------------------------

const calloutColour = (c: TurnCallout) => `var(--band-${c.band})`;

function CalloutMarker({ c, on, animate }: { c: TurnCallout; on: boolean; animate: boolean }) {
  const col = calloutColour(c);
  return (
    <>
      <circle cx={c.x} cy={c.y} r={15} fill={col} fillOpacity={0.16}>
        {animate ? (
          <>
            <animate attributeName="r" values="12;19;12" dur="1.6s" repeatCount="indefinite" />
            <animate
              attributeName="fill-opacity"
              values=".26;.05;.26"
              dur="1.6s"
              repeatCount="indefinite"
            />
          </>
        ) : null}
      </circle>
      <circle cx={c.x} cy={c.y} r={6} fill={col} stroke={on ? '#1F1B1E' : '#fff'} strokeWidth={2} />
    </>
  );
}

function CalloutLabel({ c, on }: { c: TurnCallout; on: boolean }) {
  const col = calloutColour(c);
  return (
    <>
      <rect
        width={c.lw}
        height={CALLOUT_H}
        rx={5}
        fill="#1F1B1E"
        stroke={col}
        strokeWidth={on ? 2.4 : 1.4}
      />
      <circle cx={12} cy={12} r={4} fill={col} />
      <text x={22} y={16} fontSize={11.5} fill="#fff" fontFamily="IBM Plex Sans,sans-serif">
        {c.label}
      </text>
    </>
  );
}

// ---------------------------------------------------------------------------
// Turntable
// ---------------------------------------------------------------------------

export interface TurntableProps {
  machine: TurnMachine;
  /** This machine's condition register rows. */
  health: readonly ComponentHealth[];
  /** The highlighted component: glowing on the frame, its callout outlined. */
  selected?: string | null;
  /** Called with the component name when a callout is clicked or activated. */
  onSelectComponent?: (component: string) => void;
  className?: string;
}

/**
 * Demo `class Turntable` (lines 1764–1826). Renders nothing for a model
 * with no frames; callers check {@link hasTurntable} first.
 */
export function Turntable({
  machine,
  health,
  selected = null,
  onSelectComponent,
  className,
}: TurntableProps) {
  const model = turntableModel(machine.model);
  const { manifest, error } = useTurntableManifest(model);
  const { brandId } = useBrand();
  const plan = liveryPlan(brandId, driveKind(machine));
  const reduced = useReducedMotion();

  const [exploded, setExploded] = useState(false);
  const [explodeWanted, setExplodeWanted] = useState(false);
  const [pos, setPos] = useState<number | null>(null);
  const [spinning, setSpinning] = useState(false);
  const [shown, setShown] = useState(false);
  const [offsets, setOffsets] = useState<Record<string, CalloutOffset>>(
    () => TURNPOS.get(machine.machine_id) ?? {},
  );
  const [drag, setDrag] = useState<{ component: string; lx: number; ly: number } | null>(null);

  const stageRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const glowRef = useRef<HTMLCanvasElement>(null);
  const getImage = useImageCache();

  // A new machine starts over at the side elevation.
  useEffect(() => {
    setExploded(false);
    setExplodeWanted(false);
    setPos(null);
    setSpinning(false);
    setShown(false);
    setOffsets(TURNPOS.get(machine.machine_id) ?? {});
  }, [machine.machine_id]);

  const set: FrameSet | null = manifest
    ? exploded && manifest.explode
      ? manifest.explode
      : manifest
    : null;
  const i = set ? (pos ?? frameIndex(TURN_HOME_DEG, set)) % set.n : 0;
  const angle = set ? frameAngle(i, set) : TURN_HOME_DEG;

  const baseSrcs = useMemo(
    () =>
      manifest && model
        ? Array.from({ length: manifest.n }, (_, k) => turntableFrameUrl(model, 'f', k))
        : null,
    [manifest, model],
  );
  const explodeSrcs = useMemo(
    () =>
      manifest?.explode && model && explodeWanted
        ? Array.from({ length: manifest.explode.n }, (_, k) => turntableFrameUrl(model, 'x', k))
        : null,
    [manifest, model, explodeWanted],
  );
  const base = useLiveryUrls(baseSrcs, plan, brandId);
  const boom = useLiveryUrls(explodeSrcs, plan, brandId);
  usePreload(base.urls);
  usePreload(boom.urls);

  const urls = exploded ? boom.urls : base.urls;
  const frameFilter = exploded ? boom.filter : base.filter;
  const frameSrc = urls && set ? urls[i] : null;
  const ready = Boolean(frameSrc) && shown;

  /** Move to frame `k` of the current set. */
  const go = useCallback((k: number) => setPos(k), []);
  const step = useCallback(
    (k: number) => {
      if (!set) return;
      setPos((p) => stepFrame(p ?? frameIndex(TURN_HOME_DEG, set), k, set.n));
    },
    [set],
  );

  // Demo line 1792. Auto-rotate; never under reduced motion.
  useEffect(() => {
    if (!spinning || reduced || !ready) return;
    const t = window.setInterval(() => step(1), exploded ? SPIN_MS_EXPLODED : SPIN_MS);
    return () => window.clearInterval(t);
  }, [spinning, reduced, ready, exploded, step]);
  useEffect(() => {
    if (reduced) setSpinning(false);
  }, [reduced]);

  /** Demo line 1794. Keep the angle across the swap. */
  const toggleExplode = () => {
    if (!manifest?.explode) return;
    const next = !exploded;
    const nextSet = next ? manifest.explode : manifest;
    setExplodeWanted(true);
    setSpinning(false);
    setExploded(next);
    setPos(frameIndex(angle, nextSet));
  };

  // -- drag / wheel / keys --------------------------------------------------
  const dragState = useRef<{ x: number } | null>(null);
  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if ((e.target as Element).closest('[data-callout]')) return;
    dragState.current = { x: e.clientX };
    e.currentTarget.setPointerCapture(e.pointerId);
    setSpinning(false);
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = dragState.current;
    if (!d) return;
    const stepPx = exploded ? DRAG_STEP_EXPLODED : DRAG_STEP;
    const dx = e.clientX - d.x;
    if (Math.abs(dx) >= stepPx) {
      const k = Math.trunc(dx / stepPx);
      step(-k);
      d.x += k * stepPx;
    }
  };
  const onPointerUp = () => {
    dragState.current = null;
  };

  const wheelAcc = useRef(0);
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      const dx = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.shiftKey ? e.deltaY : 0;
      if (!dx) return;
      e.preventDefault();
      setSpinning(false);
      wheelAcc.current += dx;
      const k = Math.trunc(wheelAcc.current / WHEEL_STEP);
      if (k) {
        step(k);
        wheelAcc.current -= k * WHEEL_STEP;
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [step]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget || !set) return;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      setSpinning(false);
      step(e.key === 'ArrowLeft' ? -1 : 1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      setSpinning(false);
      go(frameIndex(TURN_HOME_DEG, set));
    }
  };

  // -- glow -----------------------------------------------------------------
  const target = manifest ? maskTarget(manifest.compidx, selected) : null;
  const maskSrc =
    set && model && frameSrc ? turntableFrameUrl(model, exploded ? 'xm' : 'm', i) : null;
  useEffect(() => {
    const cv = glowRef.current;
    if (!cv) return;
    if (target === null || !maskSrc || !selected) {
      cv.style.display = 'none';
      return;
    }
    const mask = getImage(maskSrc);
    let live = true;
    const draw = () => {
      if (!live) return;
      const n = drawGlow(cv, mask, target, glowRgb(cv, health, selected));
      cv.style.display = n ? 'block' : 'none';
    };
    if (mask.complete && mask.naturalWidth) draw();
    else mask.addEventListener('load', draw, { once: true });
    return () => {
      live = false;
      mask.removeEventListener('load', draw);
    };
  }, [target, maskSrc, selected, health, getImage]);

  // -- callouts -------------------------------------------------------------
  const flagged = useMemo(() => flaggedComponents(health), [health]);
  const callouts = useMemo(() => {
    if (!manifest || !set) return [];
    return layoutCallouts(flagged, set.anchors[i] ?? {}, manifest.w, manifest.h, offsets);
  }, [manifest, set, i, flagged, offsets]);

  const toImg = (e: ReactPointerEvent<Element>) => {
    const ctm = svgRef.current?.getScreenCTM();
    if (!ctm) return null;
    return new DOMPoint(e.clientX, e.clientY).matrixTransform(ctm.inverse());
  };
  const labelDrag = useRef<{
    component: string;
    x: number;
    y: number;
    ox: number;
    oy: number;
    moved: boolean;
  } | null>(null);
  const onLabelDown = (c: TurnCallout) => (e: ReactPointerEvent<SVGGElement>) => {
    e.stopPropagation();
    const p = toImg(e);
    if (!p) return;
    labelDrag.current = {
      component: c.component,
      x: p.x,
      y: p.y,
      ox: c.lx,
      oy: c.ly,
      moved: false,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onLabelMove = (e: ReactPointerEvent<SVGGElement>) => {
    const st = labelDrag.current;
    if (!st) return;
    const p = toImg(e);
    if (!p) return;
    if (Math.hypot(p.x - st.x, p.y - st.y) > 3) st.moved = true;
    if (st.moved)
      setDrag({ component: st.component, lx: st.ox + (p.x - st.x), ly: st.oy + (p.y - st.y) });
  };
  const onLabelUp = (c: TurnCallout) => () => {
    const st = labelDrag.current;
    labelDrag.current = null;
    if (!st) return;
    if (st.moved && drag && drag.component === c.component) {
      const next = {
        ...offsets,
        [c.component]: { dx: drag.lx + c.lw / 2 - c.x, dy: drag.ly - c.y },
      };
      TURNPOS.set(machine.machine_id, next);
      setOffsets(next);
      setDrag(null);
    } else {
      setDrag(null);
      onSelectComponent?.(c.component);
    }
  };
  const activate = (component: string) => (e: KeyboardEvent<SVGGElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelectComponent?.(component);
    }
  };

  if (!model) return null;

  const near = nearestSnap(angle);
  const posterFilter = plan.paint ? LIVERY_CSS_FALLBACK[brandId] : plan.filter;
  const noExplode = manifest !== null && !manifest.explode;

  const btn =
    'inline-flex h-8 min-w-8 items-center justify-center rounded-md border border-[var(--glass-border)] px-2 text-xs text-muted transition-colors hover:bg-[var(--glass-surface-strong)] disabled:cursor-not-allowed disabled:opacity-50 aria-pressed:border-[var(--accent)] aria-pressed:bg-[var(--accent)] aria-pressed:text-[var(--accent-text)]';

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div
        ref={stageRef}
        role="group"
        tabIndex={0}
        aria-roledescription="360° viewer"
        aria-label={`${machine.machine_id} ${machine.model}, 360° studio render at ${Math.round(angle)}°. Use the left and right arrow keys to rotate, Home for the side elevation.`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onKeyDown={onKeyDown}
        className={cn(
          'relative aspect-video w-full touch-pan-y select-none overflow-hidden rounded-lg',
          'cursor-grab active:cursor-grabbing',
          'bg-[radial-gradient(ellipse_at_50%_70%,#ffffff_0%,#f3f0ee_60%,#e9e5e2_100%)]',
          'dark:bg-[radial-gradient(ellipse_at_50%_70%,#2a2530_0%,#1f1b24_60%,#17141a_100%)]',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]',
        )}
      >
        {!ready ? (
          <img
            src={posterUrl(machine)}
            alt={`${machine.machine_id} ${machine.model} side elevation, studio render`}
            draggable={false}
            width={1024}
            height={576}
            className="pointer-events-none absolute inset-0 h-full w-full object-contain"
            style={{ filter: posterFilter }}
          />
        ) : null}
        {frameSrc ? (
          <img
            src={frameSrc}
            alt={`${machine.machine_id} ${machine.model} studio render, ${Math.round(angle)}°${exploded ? ', exploded' : ''}`}
            draggable={false}
            onLoad={() => setShown(true)}
            className={cn(
              'pointer-events-none absolute inset-0 h-full w-full object-contain',
              shown ? 'opacity-100' : 'opacity-0',
            )}
            style={{ filter: frameFilter }}
          />
        ) : null}
        <canvas
          ref={glowRef}
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 h-full w-full object-contain motion-safe:animate-pulse"
          style={{ display: 'none' }}
        />
        {manifest && ready ? (
          <svg
            ref={svgRef}
            viewBox={`0 0 ${manifest.w} ${manifest.h}`}
            className="absolute inset-0 h-full w-full"
            aria-label="Flagged components"
          >
            {callouts.map((c) => {
              const lx = drag?.component === c.component ? drag.lx : c.lx;
              const ly = drag?.component === c.component ? drag.ly : c.ly;
              return (
                <line
                  key={`ld-${c.component}`}
                  x1={c.x}
                  y1={c.y}
                  x2={lx + c.lw / 2}
                  y2={ly + 13}
                  stroke={calloutColour(c)}
                  strokeWidth={1.2}
                  strokeDasharray="3 3"
                />
              );
            })}
            {callouts.map((c) => (
              <g
                key={`hs-${c.component}`}
                data-callout
                role="button"
                tabIndex={0}
                aria-label={`${c.label}. Show its history.`}
                aria-pressed={selected === c.component}
                className="cursor-pointer outline-none [&:focus-visible>circle:last-child]:stroke-[var(--accent)]"
                onClick={() => onSelectComponent?.(c.component)}
                onKeyDown={activate(c.component)}
              >
                <title>{c.label}</title>
                <CalloutMarker c={c} on={selected === c.component} animate={!reduced} />
              </g>
            ))}
            {callouts.map((c) => {
              const lx = drag?.component === c.component ? drag.lx : c.lx;
              const ly = drag?.component === c.component ? drag.ly : c.ly;
              return (
                <g
                  key={`hl-${c.component}`}
                  data-callout
                  role="button"
                  tabIndex={0}
                  aria-label={`${c.label}. Show its history; drag to move the label.`}
                  transform={`translate(${lx},${ly})`}
                  className="cursor-move outline-none [&:focus-visible>rect]:stroke-[var(--accent)]"
                  onPointerDown={onLabelDown(c)}
                  onPointerMove={onLabelMove}
                  onPointerUp={onLabelUp(c)}
                  onPointerCancel={() => {
                    labelDrag.current = null;
                    setDrag(null);
                  }}
                  onKeyDown={activate(c.component)}
                >
                  <CalloutLabel c={c} on={selected === c.component} />
                </g>
              );
            })}
          </svg>
        ) : null}
        <span className="pointer-events-none absolute bottom-2 right-2.5 rounded bg-[var(--glass-surface-strong)] px-1.5 py-0.5 text-[10.5px] text-muted opacity-90">
          {error
            ? '360° frames unavailable — showing the side elevation'
            : ready
              ? 'drag to rotate'
              : 'loading 360° frames…'}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-muted">
        <button
          type="button"
          className={btn}
          title="Rotate left"
          aria-label="Rotate left"
          disabled={!ready}
          onClick={() => {
            setSpinning(false);
            step(-1);
          }}
        >
          ◀
        </button>
        <button
          type="button"
          className={btn}
          title="Rotate right"
          aria-label="Rotate right"
          disabled={!ready}
          onClick={() => {
            setSpinning(false);
            step(1);
          }}
        >
          ▶
        </button>
        <button
          type="button"
          className={btn}
          aria-pressed={spinning}
          disabled={!ready || reduced}
          title={reduced ? 'Spin is off — your system asks for reduced motion' : 'Spin the machine'}
          onClick={() => setSpinning((s) => !s)}
        >
          Spin
        </button>
        <button
          type="button"
          className={btn}
          aria-pressed={exploded}
          disabled={!ready || noExplode}
          title={
            noExplode
              ? 'Exploded view not rendered for this model'
              : 'Separate the component groups'
          }
          onClick={toggleExplode}
        >
          Explode
        </button>
        <span
          className="mono min-w-[34px] text-center text-zinc-700 dark:text-zinc-300"
          aria-live="polite"
        >
          {Math.round(angle)}°
        </span>
        <span className="flex-1" />
        <span>
          {exploded ? 'Exploded · component groups separated · ' : ''}
          {flagged.length} flagged · drag a label to move it
        </span>
      </div>

      <div
        className="grid grid-cols-4 gap-1.5 @2xl:grid-cols-8"
        role="group"
        aria-label="Snapshot angles"
      >
        {TURN_SNAPS.map(([deg, label]) => {
          const on = near === deg && !exploded;
          const k = manifest ? frameIndex(deg, manifest) : 0;
          const thumb = base.urls?.[k];
          return (
            <button
              key={deg}
              type="button"
              title={label}
              aria-pressed={on}
              disabled={!ready}
              onClick={() => {
                if (!manifest) return;
                setSpinning(false);
                setExploded(false);
                go(frameIndex(deg, manifest));
              }}
              className={cn(
                'flex flex-col items-center gap-px rounded-md border bg-[var(--glass-surface)] p-1 transition-colors',
                'hover:border-[var(--accent)] disabled:cursor-default disabled:hover:border-[var(--glass-border)]',
                on
                  ? 'border-[var(--accent)] shadow-[0_0_0_2px_var(--accent-soft)]'
                  : 'border-[var(--glass-border)]',
              )}
            >
              {thumb ? (
                <img
                  src={thumb}
                  alt=""
                  loading="lazy"
                  className="block aspect-video w-full object-contain"
                  style={{ filter: base.filter }}
                />
              ) : (
                <span className="block aspect-video w-full" aria-hidden="true" />
              )}
              <span className="whitespace-nowrap text-[9.5px] text-muted">{label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TurntableStill
// ---------------------------------------------------------------------------

export interface TurntableStillProps {
  machine: TurnMachine;
  health: readonly ComponentHealth[];
  /** Callout this component only; `null` callouts every flagged component. */
  component?: string | null;
  className?: string;
}

/**
 * Demo lines 1826–1829. `turnStill()` — the side-elevation frame with its
 * callouts, not interactive. For alert, incident and division cards. Falls
 * back to the 2D side drawing for a model with no frames.
 */
export function TurntableStill({
  machine,
  health,
  component = null,
  className,
}: TurntableStillProps) {
  const model = turntableModel(machine.model);
  const { manifest } = useTurntableManifest(model);
  const { brandId } = useBrand();
  const plan = liveryPlan(brandId, driveKind(machine));
  const k = manifest ? frameIndex(TURN_HOME_DEG, manifest) : null;
  const srcs = useMemo(
    () => (model && k !== null ? [turntableFrameUrl(model, 'f', k)] : null),
    [model, k],
  );
  const { urls, filter } = useLiveryUrls(srcs, plan, brandId);
  const reduced = useReducedMotion();

  if (!model) {
    return hasIllustration(machine.cls) ? (
      <MachineIllustration
        machineId={machine.machine_id}
        model={machine.model}
        cls={machine.cls}
        view="side"
        health={health}
        selected={component}
      />
    ) : null;
  }

  const callouts =
    manifest && k !== null
      ? stillCallouts(health, manifest.anchors[k] ?? {}, manifest.w, manifest.h, component)
      : [];
  const src = urls?.[0] ?? null;

  return (
    <div
      className={cn(
        'relative aspect-video w-full overflow-hidden rounded-lg',
        'bg-[radial-gradient(ellipse_at_50%_70%,#ffffff_0%,#f3f0ee_60%,#e9e5e2_100%)]',
        'dark:bg-[radial-gradient(ellipse_at_50%_70%,#2a2530_0%,#1f1b24_60%,#17141a_100%)]',
        className,
      )}
    >
      <img
        src={src ?? posterUrl(machine)}
        alt={`${machine.machine_id} side view`}
        width={src ? manifest?.w : 1024}
        height={src ? manifest?.h : 576}
        className="absolute inset-0 h-full w-full object-contain"
        style={{ filter: src ? filter : plan.paint ? LIVERY_CSS_FALLBACK[brandId] : plan.filter }}
      />
      {manifest && src ? (
        <svg
          viewBox={`0 0 ${manifest.w} ${manifest.h}`}
          className="absolute inset-0 h-full w-full"
          aria-hidden="true"
        >
          {callouts.map((c) => (
            <g key={c.component}>
              <line
                x1={c.x}
                y1={c.y}
                x2={c.lx + c.lw / 2}
                y2={c.ly + 13}
                stroke={calloutColour(c)}
                strokeWidth={1.2}
                strokeDasharray="3 3"
              />
              <CalloutMarker c={c} on={false} animate={!reduced} />
              <g transform={`translate(${c.lx},${c.ly})`}>
                <CalloutLabel c={c} on={false} />
              </g>
            </g>
          ))}
        </svg>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// MachineThumb
// ---------------------------------------------------------------------------

export interface MachineThumbProps {
  machine: TurnMachine;
  className?: string;
}

/**
 * Demo line 1830. `turnIcon()` — the model's small turntable icon, in the
 * brand livery. Falls back to the 2D side drawing (no callouts) when the
 * model has no frames or the icon fails to load.
 */
export function MachineThumb({ machine, className }: MachineThumbProps) {
  const model = turntableModel(machine.model);
  const { brandId } = useBrand();
  const plan = liveryPlan(brandId, driveKind(machine));
  const srcs = useMemo(() => (model ? [turntableIconUrl(model)] : null), [model]);
  const { urls, filter } = useLiveryUrls(srcs, plan, brandId);
  const [failed, setFailed] = useState(false);

  if (!model || failed) {
    return hasIllustration(machine.cls) ? (
      <span className={cn('block', className)} aria-hidden="true">
        <MachineIllustration
          machineId={`${machine.machine_id}-thumb`}
          model={machine.model}
          cls={machine.cls}
          view="side"
          health={[]}
        />
      </span>
    ) : null;
  }

  return (
    <span className={cn('block', className)} aria-hidden="true">
      {urls ? (
        <img
          src={urls[0]}
          alt=""
          width={132}
          height={66}
          loading="lazy"
          onError={() => setFailed(true)}
          className="block h-full w-full object-contain"
          style={{ filter }}
        />
      ) : null}
    </span>
  );
}
